// src/components/SelectionModal.tsx
import React, { useState } from 'react';
import { Participant } from '../data/eurovisionData';

interface SelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  participants: Participant[];
  onSelect: (participant: Participant) => void;
  onRemove: (id: number) => void;
  alreadySelected: number[];
  activeSlotIndex: number | null;
}

export function SelectionModal({ 
  isOpen, 
  onClose, 
  participants, 
  onSelect,
  onRemove,
  alreadySelected,
  activeSlotIndex
}: SelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  
  // Create a custom close handler that resets the detail view
  const handleClose = () => {
    onClose();
    // Reset details state AFTER modal is closed
    setTimeout(() => {
      setShowDetails(false);
      setSelectedParticipant(null);
    }, 300); // Small delay to ensure modal is closed first
  };
  
  if (!isOpen) return null;
  
  // Sort participants alphabetically
  const sortedParticipants = [...participants].sort((a, b) => 
    a.country.localeCompare(b.country)
  );
  
  // Filter participants based on search term
  const filteredParticipants = searchTerm.trim() === '' 
    ? sortedParticipants
    : sortedParticipants.filter(p => 
        p.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.songTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
  // Handle viewing participant details
  const handleViewDetails = (participant: Participant) => {
    setSelectedParticipant(participant);
    setShowDetails(true);
  };
  
  // Handle selection from details view
  const handleSelectFromDetails = (participant: Participant) => {
    onSelect(participant);
    handleClose();
  };
  
  // Handle remove with proper logging
  const handleRemove = (id: number) => {
    console.log("Removing participant with ID:", id);
    onRemove(id);
  };
      
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-30 flex items-center justify-center p-2 md:p-8">
      <div className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-[95%] md:max-w-2xl max-h-[95vh]">
        {/* Modal header */}
        <div className="bg-white border-b text-black p-4 flex justify-between items-center">
          {showDetails && (
            <button
              onClick={() => setShowDetails(false)}
              className="text-black hover:text-gray-500 mr-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          
          <h2 className="text-lg font-bold flex-grow">
            {showDetails 
              ? selectedParticipant?.country
              : activeSlotIndex !== null
                ? `Select for Position #${activeSlotIndex + 1}`
                : 'Select a Participant'
            }
          </h2>
          
          <button 
            onClick={handleClose}
            className="text-black hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* If showing details */}
        {showDetails ? (
          <div className="p-4 overflow-y-auto" style={{ height: 'calc(90vh - 4rem)' }}>
            {selectedParticipant && (
              <div>
                <div className="flex items-center mb-4">
                  <img 
                    src={`${process.env.PUBLIC_URL}/${selectedParticipant.image}`}
                    alt={selectedParticipant.name} 
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="ml-3">
                    <h3 className="font-bold">{selectedParticipant.country}</h3>
                    <div className="text-sm">{selectedParticipant.name}</div>
                    <div className="text-xs text-gray-500">{selectedParticipant.songTitle}</div>
                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded inline-flex items-center mt-1">
                      <span className="mr-1">Odds to win:</span>
                      <span className="font-bold">{selectedParticipant.odds.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-gray-700">
                    {selectedParticipant.description}
                  </p>
                </div>
                
                {/* Video embed */}
                {selectedParticipant.videoId && (
                  <div className="aspect-video mb-4 bg-black rounded overflow-hidden">
                    <iframe 
                      src={`https://www.youtube.com/embed/${selectedParticipant.videoId}`}
                      title={`${selectedParticipant.country} - ${selectedParticipant.songTitle}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                )}
                
                {/* Action button */}
                <div className="mt-4">
                  {alreadySelected.includes(selectedParticipant.id) ? (
                    <button 
                      onClick={() => {
                        handleRemove(selectedParticipant.id);
                        handleClose();
                      }}
                      className="w-full py-2 bg-red-600 text-white rounded font-medium hover:bg-red-700"
                    >
                      Remove from Ranking
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleSelectFromDetails(selectedParticipant)}
                      className="w-full py-2 bg-pink-600 text-white rounded font-medium hover:bg-pink-700"
                    >
                      Add to Position #{activeSlotIndex !== null ? activeSlotIndex + 1 : ''}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Selection view */}
            <div className="p-4">
              {/* View mode toggle */}
              <div className="flex items-center justify-between items-center gap-x-4 mb-4">
                {/* Search input */}
              <div className="relative grow">
                <input
                  type="text"
                  className="text-sm md:text-base w-full p-2 pl-8 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Countries, artists, songs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg 
                  className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
                <div className="flex border rounded overflow-hidden">
                  <button 
                    className={`p-2 w-8 md:w-10 flex justify-center ${viewMode === 'list' ? 'bg-gray-500 text-white' : 'bg-gray-100'}`}
                    onClick={() => setViewMode('list')}
                    title="List View"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  <button 
                    className={`p-2 w-8 md:w-10 flex justify-center ${viewMode === 'grid' ? 'bg-gray-500 text-white' : 'bg-gray-100'}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Scrollable content area - take up most of modal height */}
              <div className="overflow-y-auto max-h-[calc(90vh-8rem)] overflow-x-hidden">
                {/* Grid View - 5 columns */}
                {viewMode === 'grid' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                    {filteredParticipants.map(participant => {
                      const isSelected = alreadySelected.includes(participant.id);
                      
                      return (
                        <div 
                          key={participant.id} 
                          className={`bg-white rounded-lg border shadow-sm overflow-hidden ${
                            isSelected ? 'bg-pink-50 border-pink-200' : 'border-transparent'
                          }`}
                        >
                          <div className="relative">
                            <img 
                              src={`${process.env.PUBLIC_URL}/${participant.image}`}  
                              alt={participant.name}
                              className="w-full aspect-square object-cover"
                            />
                            <div className="absolute bottom-1 right-1">
                              <img 
                                src={`${process.env.PUBLIC_URL}/flags/${participant.countryCode}.png`} 
                                alt={`${participant.country} flag`}
                                className="w-8 h-8"
                              />
                            </div>
                          </div>
                          
                          {/* Vertical layout for info */}
                          <div className="p-2">
                            <h4 className="font-bold text-sm truncate">{participant.country}</h4>
                            <p className="text-sm text-gray-600 truncate">{participant.name}</p>
                            <p className="text-sm text-gray-500 truncate">{participant.songTitle}</p>
                            
                            {/* Odds below text info */}
                            <div className="mt-1">
                              <span className="bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded inline-flex items-center">
                                <span className="mr-1 text-xs">Odds to win:</span>
                                <span className="font-bold text-[11px] md:text-xs">{participant.odds.toFixed(2)}</span>
                              </span>
                            </div>
                            
                            {/* Action buttons at bottom */}
                            <div className="flex justify-center mt-2 space-x-2">
                              <button
                                onClick={() => handleViewDetails(participant)}
                                className="p-1 text-blue-600 rounded hover:bg-blue-50"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              
                              {!isSelected ? (
                                <button
                                  onClick={() => {
                                    onSelect(participant);
                                    handleClose();
                                  }}
                                  className="p-1 text-pink-600 rounded hover:bg-pink-50"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    handleRemove(participant.id);
                                  }}
                                  className="p-1 text-red-600 rounded hover:bg-red-50"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* List View - responsive layout */}
                {viewMode === 'list' && (
                  <div className="divide-y divide-gray-200">
                    {filteredParticipants.map(participant => {
                      const isSelected = alreadySelected.includes(participant.id);
                      
                      return (
                        <div 
                          key={participant.id} 
                          className={`p-2 ${isSelected ? 'bg-pink-50' : ''}`}
                        >
                          {/* Mobile view - stacked layout */}
                          <div className="md:hidden flex items-start justify-between">
                            <div className="flex items-start">
                                <div className="relative flex-shrink-0 mr-4">
                                <img 
                                    src={`${process.env.PUBLIC_URL}/${participant.image}`}
                                    alt={participant.name}
                                    className="w-20 h-20 object-cover rounded"
                                />
                                <div className="absolute -bottom-1 -right-1">
                                    <img 
                                    src={`${process.env.PUBLIC_URL}/flags/${participant.countryCode}.png`} 
                                    alt={`${participant.country} flag`}
                                    className="w-5 h-5"
                                    />
                                </div>
                                </div>
                                
                                <div className="flex-grow min-w-0">
                                <div className="font-bold text-sm truncate">{participant.country}</div>
                                <div className="text-xs text-gray-600 truncate">{participant.name}</div>
                                <div className="text-xs text-gray-500 truncate">{participant.songTitle}</div>
                                
                                {/* Odds below text on mobile */}
                                <div className="mt-1 mb-1">
                                    <span className="bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded inline-flex items-center">
                                    <span className="mr-1 text-xs">Odds to win:</span>
                                    <span className="font-bold text-[11px] md:text-sm">{participant.odds.toFixed(2)}</span>
                                    </span>
                                </div>
                                </div>
                            </div>
  
                            {/* Action buttons on the right side */}
                            <div className="self-center flex flex-col space-y-2">
                                <button
                                onClick={() => handleViewDetails(participant)}
                                className="p-1 text-blue-600 rounded hover:bg-blue-50"
                                >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                </button>
                                
                                {isSelected ? (
                                <button
                                    onClick={() => handleRemove(participant.id)}
                                    className="p-1 text-red-600 rounded hover:bg-red-50"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                ) : (
                                <button
                                    onClick={() => {
                                    onSelect(participant);
                                    handleClose();
                                    }}
                                    className="p-1 text-pink-600 rounded hover:bg-pink-50"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                </button>
                                )}
                            </div>
                            </div>
                          
                          {/* Desktop view - original layout */}
                          <div className="hidden md:flex items-center">
                            <div className="relative flex-shrink-0 mr-4">
                              <img 
                                src={`${process.env.PUBLIC_URL}/${participant.image}`}
                                alt={participant.name}
                                className="w-16 h-16 object-cover rounded"
                              />
                              <div className="absolute -bottom-1 -right-1">
                                <img 
                                  src={`${process.env.PUBLIC_URL}/flags/${participant.countryCode}.png`} 
                                  alt={`${participant.country} flag`}
                                  className="w-5 h-5"
                                />
                              </div>
                            </div>
                            
                            <div className="flex-grow">
                              <div className="font-bold text-sm">{participant.country}</div>
                              <div className="text-sm text-gray-600 truncate">{participant.name}</div>
                              <div className="text-xs text-gray-500 truncate">{participant.songTitle}</div>
                            </div>
                            
                            <div className="flex-shrink-0 mr-2">
                              <span className="bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded inline-flex items-center">
                                <span className="mr-1">Odds to win:</span>
                                <span className="font-bold">{participant.odds.toFixed(2)}</span>
                              </span>
                            </div>
                            
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleViewDetails(participant)}
                                className="p-1 text-blue-600 rounded hover:bg-blue-50"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              
                              {isSelected ? (
                                <button
                                  onClick={() => handleRemove(participant.id)}
                                  className="p-1 text-red-600 rounded hover:bg-red-50"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    onSelect(participant);
                                    handleClose();
                                  }}
                                  className="p-1 text-pink-600 rounded hover:bg-pink-50"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {filteredParticipants.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    No participants match your search
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}